import '@anthem/uxd/util';
import * as i0 from '@angular/core';
import { Component, HostBinding, Input, HostListener, EventEmitter, Output, ContentChildren, forwardRef, ViewChild, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';

/**
 * Parent template Carousel Selector component.
 * Extend this component to create your own scrollable carousel component
 */
//istanbul ignore next
class CarouselTemplateComponent {
    constructor() {
        this.carouselClass = true;
        this.showNavigations = true;
        this._items = [];
        this._selectedIndex = -1;
        this._viewMode = -1;
        this.visibleStartIndex = 0;
        this.visibleEndIndex = 0;
        this._focusIndex = 0;
        this.scrollPos = 0; //Used to bind in every child li element.  [style.left.%]="scrollPos"
        this.disableRight = true;
        this.disableLeft = true;
    }
    static { this.DESKTOP = 1; }
    static { this.TABLET = 2; }
    static { this.MOBILE = 3; }
    ngAfterViewInit() {
        if (this.items.length > 0) {
            this.selectedIndex = 0;
        }
    }
    onResize(event) {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            this.calculateViewMode();
            //update scrollPos
            this.resetScroll();
        }, 300);
    }
    onOrientationChange(event) {
        this.resetScroll();
    }
    //Reset scroll to initial position
    resetScroll() {
        this._viewMode = -1;
        this.scrollPos = 0;
        this.visibleStartIndex = 0;
        this.visibleEndIndex = 0;
        this.calculateViewMode();
    }
    /**
     * Based on window width calculate the viewMode
     * Possible viewMode value are DESKTOP, TABLET and MOBILE
     */
    calculateViewMode() {
        // Get the width of the window - responsive, set viewMode
        let windowWidth = window.innerWidth;
        if (windowWidth <= 479) {
            this.viewMode = CarouselTemplateComponent.MOBILE;
        }
        else if (windowWidth > 479 && windowWidth <= 1023) {
            this.viewMode = CarouselTemplateComponent.TABLET;
        }
        else if (windowWidth > 1023) {
            this.viewMode = CarouselTemplateComponent.DESKTOP;
        }
    }
    set viewMode(val) {
        if (this._viewMode !== val) {
            //set viewMode value
            this._viewMode = val;
            //When viewMode changes
            //Then
            //Re-calculate endIndex
            this.visibleEndIndex = this.visibleStartIndex + this.getNumOfVisibleItems() - 1;
            //Then
            //Re-calculate arrow controls visibility
            this.updateArrowsVisibility();
        }
    }
    get viewMode() {
        return this._viewMode;
    }
    updateArrowsVisibility() {
        let n = this.items.length;
        this.disableRight = true;
        this.disableLeft = true;
        if (this.visibleStartIndex > 0) {
            this.disableLeft = false;
        }
        if (n - 1 > this.visibleEndIndex) {
            this.disableRight = false;
        }
    }
    /**
     * Returns number of visible children (based on the viewMode) in the carousel.
     * This method can be overridden in the extended classes.
     */
    getNumOfVisibleItems() {
        if (this.viewMode === CarouselTemplateComponent.MOBILE) {
            return 1;
        }
        if (this.viewMode === CarouselTemplateComponent.TABLET) {
            return 2;
        }
        //default for Desktop
        return 3;
    }
    set items(val) {
        if (val instanceof Array && this._items !== val) {
            this._items = val;
            this._selectedIndex = -1;
            this.selectedIndex = 0;
            // Reset scroll
            this.resetScroll();
        }
    }
    get items() {
        return this._items;
    }
    set selectedIndex(val) {
        if (this._selectedIndex !== val) {
            this._selectedIndex = val;
            this._focusIndex = val;
            this._prevFocusIndex = val;
            this.onSelectedIndexChange(val);
        }
    }
    get selectedIndex() {
        return this._selectedIndex;
    }
    onSelectedIndexChange(selectedIndex) {
        //You can override this method in your class to emit events.
    }
    onChildFocus(index) {
        clearTimeout(this._focusTimer);
        //When the focused child belongs out-side-of-visible children
        //Then perform scrolling
        if (index < this.visibleStartIndex || index > this.visibleEndIndex) {
            this._focusIndex = index;
            let delta = this._focusIndex - this._prevFocusIndex;
            let direction = delta === 1 ? 'right' : delta === -1 ? 'left' : undefined;
            this.scroll(direction, false);
        }
        this._prevFocusIndex = this._focusIndex;
    }
    onChildBlur(index) {
        this._prevFocusIndex = index;
        clearTimeout(this._focusTimer);
        this._focusTimer = setTimeout(() => {
            //When No Child is focused within 100 ms, let us assume that Focus is moved of this Carousel
            //Then
            //Reset focusing index
        }, 100);
    }
    scroll(direction, updateScrollPos = true) {
        let unitScrollSize = 100 / this.getNumOfVisibleItems();
        if (direction === 'right') {
            if (this.visibleEndIndex < this.items.length - 1) {
                this.visibleStartIndex += 1;
                this.visibleEndIndex += 1;
                if (updateScrollPos) {
                    this.scrollPos -= unitScrollSize;
                }
                this.updateArrowsVisibility();
            }
        }
        else if (direction === 'left') {
            if (this.visibleStartIndex > 0) {
                this.visibleStartIndex -= 1;
                this.visibleEndIndex -= 1;
                if (updateScrollPos) {
                    this.scrollPos += unitScrollSize;
                }
                this.updateArrowsVisibility();
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CarouselTemplateComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CarouselTemplateComponent, selector: "[data-uxd-carousel-template-cmp]", inputs: { showNavigations: "showNavigations", items: "items", selectedIndex: "selectedIndex" }, host: { listeners: { "window:resize": "onResize($event)", "window:orientationchange": "onOrientationChange($event)" }, properties: { "class.carousel-selector-cmp": "this.carouselClass" } }, ngImport: i0, template: '' // Parent class - extend in sub-classes
        , isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CarouselTemplateComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-carousel-template-cmp]',
                    template: '' // Parent class - extend in sub-classes
                }]
        }], propDecorators: { carouselClass: [{
                type: HostBinding,
                args: ['class.carousel-selector-cmp']
            }], showNavigations: [{
                type: Input
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }], onOrientationChange: [{
                type: HostListener,
                args: ['window:orientationchange', ['$event']]
            }], items: [{
                type: Input
            }], selectedIndex: [{
                type: Input
            }] } });

/**
 * Single carousel tile child component
 */
class ButtonSelectorCarouselTileComponent {
    constructor() {
        this.buttonSelectItem = true;
        this.onButtonCarouselSelect = new EventEmitter();
    }
    ngAfterContentChecked() {
        if (!this.buttonSelectorData.activeIndex) {
            this.buttonSelectorData.activeIndex = 0;
        }
        if (!this.buttonSelectorData.ariaBtnDescribedby) {
            this.buttonSelectorData.ariaBtnDescribedby = "";
        }
        if (this.buttonSelectorData.activeIndex === -1) {
            this.currentlySelected = false;
        }
        this.analyticsText =
            this.buttonSelectorData.analyticsText || "contractItem";
        this.activeIndex = this.buttonSelectorData.activeIndex;
        this.currentlySelected = this.activeIndex === this.tileIndex;
        this.bindLeftPos = this._scrollPos;
    }
    btnSelectClick() {
        if (this.activeIndex !== this.tileIndex &&
            this.buttonSelectorData.activeIndex !== -1) {
            this.buttonSelectorData.activeIndex = this.tileIndex;
            this.activeIndex = this.buttonSelectorData.activeIndex;
        }
        this.emitButtonSelectClick();
    }
    emitButtonSelectClick() {
        if (this.buttonSelectorData) {
            let evtObj = {
                clickedIndex: this.tileIndex,
                btnSelectTileData: this.buttonSelectorData.items[this.tileIndex]
            };
            this.onButtonCarouselSelect.emit(evtObj);
        }
    }
    onTileFocus(index) {
        let visibleStartIndex = 0;
        let visibleEndIndex = visibleStartIndex + this._maxVisible - 1;
        if (index < visibleStartIndex || index > visibleEndIndex) {
            let delta = index - this.buttonSelectorData.prevFocusIndex;
            let direction = delta === 1 ? "right" : delta === -1 ? "left" : undefined;
            let directionObj = {
                dirString: direction
            };
        }
    }
    onTileBlur(index) {
        this.buttonSelectorData.prevFocusIndex = index;
    }
    set scrollPos(leftPerc) {
        this._scrollPos = leftPerc;
    }
    get scrollPos() {
        return this._scrollPos;
    }
    set activeIndex(indexClicked) {
        this._activeIndex = indexClicked;
    }
    get activeIndex() {
        return this._activeIndex;
    }
    set maxVisible(maxVisibleOnViewport) {
        this._maxVisible = maxVisibleOnViewport;
    }
    get maxVisible() {
        return this._maxVisible;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ButtonSelectorCarouselTileComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ButtonSelectorCarouselTileComponent, selector: "[data-uxd-button-selector-carousel-tile-cmp]", inputs: { buttonSelectorData: "buttonSelectorData", tileIndex: "tileIndex" }, outputs: { onButtonCarouselSelect: "onButtonCarouselSelect" }, host: { properties: { "class.button-select-item": "this.buttonSelectItem", "class.active": "this.currentlySelected", "style.left.%": "this.bindLeftPos" } }, ngImport: i0, template: `
    <li class="button-select-content-container" tabindex="0" [attr.data-analytics]="analyticsText + (tileIndex+1)"
    (keydown.enter)="btnSelectClick()" (keyup.space)="btnSelectClick()"
    (focus)="onTileFocus(tileIndex)" (blur)="onTileBlur(tileIndex)"
    #CarouselItem [attr.aria-selected] ="tileIndex === activeIndex" [id]="'uxd-button-select-content-container-list-item-' + tileIndex">
      <div [id]="'ant-button-select-carousel-' + tileIndex">

        <!-- Transcluded content -->
        <ng-content></ng-content>

      </div>

      <!-- Button -->
      <div class="button-select-carousel-button" [attr.aria-hidden]="tileIndex === activeIndex" [attr.data-analytics]="analyticsText + (tileIndex+1)">
        <button tabindex="-1" [id]="'ant-button-select-carousel-button-' + tileIndex" class="btn btn-primary btn-sm"
          data-lastfocus [attr.data-analytics]="analyticsText + (tileIndex+1)" [innerText]="buttonSelectorData.btnText" aria-describedby="ant-button-selector-button-aria-text" (click)="btnSelectClick()"></button>
        <span [attr.data-analytics]="analyticsText + (tileIndex+1)" class="sr-only" id="ant-button-selector-button-aria-text" [innerText]="buttonSelectorData.ariaBtnDescribedby"></span>
      </div>
    </li>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ButtonSelectorCarouselTileComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "[data-uxd-button-selector-carousel-tile-cmp]",
                    // styles: '[style.left.%]'= "scrollPos",
                    template: `
    <li class="button-select-content-container" tabindex="0" [attr.data-analytics]="analyticsText + (tileIndex+1)"
    (keydown.enter)="btnSelectClick()" (keyup.space)="btnSelectClick()"
    (focus)="onTileFocus(tileIndex)" (blur)="onTileBlur(tileIndex)"
    #CarouselItem [attr.aria-selected] ="tileIndex === activeIndex" [id]="'uxd-button-select-content-container-list-item-' + tileIndex">
      <div [id]="'ant-button-select-carousel-' + tileIndex">

        <!-- Transcluded content -->
        <ng-content></ng-content>

      </div>

      <!-- Button -->
      <div class="button-select-carousel-button" [attr.aria-hidden]="tileIndex === activeIndex" [attr.data-analytics]="analyticsText + (tileIndex+1)">
        <button tabindex="-1" [id]="'ant-button-select-carousel-button-' + tileIndex" class="btn btn-primary btn-sm"
          data-lastfocus [attr.data-analytics]="analyticsText + (tileIndex+1)" [innerText]="buttonSelectorData.btnText" aria-describedby="ant-button-selector-button-aria-text" (click)="btnSelectClick()"></button>
        <span [attr.data-analytics]="analyticsText + (tileIndex+1)" class="sr-only" id="ant-button-selector-button-aria-text" [innerText]="buttonSelectorData.ariaBtnDescribedby"></span>
      </div>
    </li>
  `
                }]
        }], propDecorators: { buttonSelectItem: [{
                type: HostBinding,
                args: ["class.button-select-item"]
            }], buttonSelectorData: [{
                type: Input
            }], tileIndex: [{
                type: Input
            }], onButtonCarouselSelect: [{
                type: Output
            }], currentlySelected: [{
                type: HostBinding,
                args: ["class.active"]
            }], bindLeftPos: [{
                type: HostBinding,
                args: ["style.left.%"]
            }] } });
//istanbul ignore next
class ButtonSelectorCarouselComponentV2 extends CarouselTemplateComponent {
    constructor() {
        super(...arguments);
        this.carouselSelectorClass = true;
    }
    ngAfterContentChecked() {
        if (this.selectorTileChildren.toArray().length > 0) {
            this._btnSelectorData = this.selectorTileChildren.toArray()[0].buttonSelectorData;
            this.leftCtrlAnalyticsId =
                this.btnSelectorData.controlAnalyticsTextLeft || "contractsSliderLeft";
            this.rightCtrlAnalyticsId =
                this.btnSelectorData.controlAnalyticsTextRight ||
                    "contractsSliderRight";
        }
        this.selectorTileChildren.toArray().forEach(selectTile => {
            selectTile.scrollPos = this.scrollPos;
            selectTile.maxVisible = this.getNumOfVisibleItems();
        });
    }
    set btnSelectorData(btnSelectDataObj) {
        this._btnSelectorData = btnSelectDataObj;
    }
    get btnSelectorData() {
        return this._btnSelectorData;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ButtonSelectorCarouselComponentV2, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ButtonSelectorCarouselComponentV2, selector: "[data-uxd-button-selector-carousel-cmp-v2]", host: { properties: { "class.carousel-selector-cmp": "this.carouselSelectorClass" } }, queries: [{ propertyName: "selectorTileChildren", predicate: ButtonSelectorCarouselTileComponent }], usesInheritance: true, ngImport: i0, template: `
    <div aria-hidden="true" #LeftControl id="ant-button-selector-carousel-left-control" class="carousel-left-control" (click)="scroll('left')" [attr.data-analytics]="leftCtrlAnalyticsId">
        <button aria-label="Move carousel left" tabindex="-1" [attr.data-analytics]="leftCtrlAnalyticsId" class="fa fa-chevron-left" [class.hidden]="disableLeft" [attr.data-analytics]="leftCtrlAnalyticsId"></button>
    </div>

    <div #ViewPort class="carousel-content-container" id="ant-button-selector-carousel-tiles-container">
      <div *ngIf="items.length > 1" class="sr-only" [innerText]=""></div>
      <!-- btnSelectorData.ariaHowTo -->
      <ul #CarouselList role="tablist" [attr.aria-multiselectable]="items.length > 1" id="ant-button-selector-carousel-list-container">

        <!-- Transcluded content -->
        <ng-content></ng-content>

      </ul>
    </div>

    <div aria-hidden="true" #RightControl id="ant-button-selector-carousel-right-control" class="carousel-right-control" (click)="scroll('right')" [attr.data-analytics]="rightCtrlAnalyticsId">
        <button aria-label="Move carousel right" tabindex="-1" class="fa fa-chevron-right" [class.hidden]="disableRight" [attr.data-analytics]="rightCtrlAnalyticsId"></button>
    </div>
`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ButtonSelectorCarouselComponentV2, decorators: [{
            type: Component,
            args: [{
                    selector: "[data-uxd-button-selector-carousel-cmp-v2]",
                    template: `
    <div aria-hidden="true" #LeftControl id="ant-button-selector-carousel-left-control" class="carousel-left-control" (click)="scroll('left')" [attr.data-analytics]="leftCtrlAnalyticsId">
        <button aria-label="Move carousel left" tabindex="-1" [attr.data-analytics]="leftCtrlAnalyticsId" class="fa fa-chevron-left" [class.hidden]="disableLeft" [attr.data-analytics]="leftCtrlAnalyticsId"></button>
    </div>

    <div #ViewPort class="carousel-content-container" id="ant-button-selector-carousel-tiles-container">
      <div *ngIf="items.length > 1" class="sr-only" [innerText]=""></div>
      <!-- btnSelectorData.ariaHowTo -->
      <ul #CarouselList role="tablist" [attr.aria-multiselectable]="items.length > 1" id="ant-button-selector-carousel-list-container">

        <!-- Transcluded content -->
        <ng-content></ng-content>

      </ul>
    </div>

    <div aria-hidden="true" #RightControl id="ant-button-selector-carousel-right-control" class="carousel-right-control" (click)="scroll('right')" [attr.data-analytics]="rightCtrlAnalyticsId">
        <button aria-label="Move carousel right" tabindex="-1" class="fa fa-chevron-right" [class.hidden]="disableRight" [attr.data-analytics]="rightCtrlAnalyticsId"></button>
    </div>
`
                }]
        }], propDecorators: { carouselSelectorClass: [{
                type: HostBinding,
                args: ["class.carousel-selector-cmp"]
            }], selectorTileChildren: [{
                type: ContentChildren,
                args: [ButtonSelectorCarouselTileComponent]
            }] } });

/**
 * Single carousel tile child component
 */
class DataDisplayCarouselTileComponent {
    constructor() {
        this.buttonSelectItem = true;
    }
    ngAfterContentChecked() {
        this.bindLeftPos = this._scrollPos;
    }
    attachAnalytics() {
        if (this.buttonSelectorData.analyticsText) {
            return this.buttonSelectorData.analyticsText;
        }
        return null;
    }
    set scrollPos(leftPerc) {
        this._scrollPos = leftPerc;
    }
    get scrollPos() {
        return this._scrollPos;
    }
    set maxVisible(maxVisibleOnViewport) {
        this._maxVisible = maxVisibleOnViewport;
    }
    get maxVisible() {
        return this._maxVisible;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DataDisplayCarouselTileComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: DataDisplayCarouselTileComponent, selector: "[data-uxd-data-display-tile-carousel-cmp]", inputs: { tileIndex: "tileIndex", buttonSelectorData: "buttonSelectorData" }, host: { properties: { "style.left.%": "this.bindLeftPos", "class.button-select-item": "this.buttonSelectItem" } }, ngImport: i0, template: `
    <li class="button-select-content-container" tabindex="0" [attr.data-analytics]="attachAnalytics() + tileIndex"
    #CarouselItem [id]="'uxd-button-select-content-container-list-item-' + tileIndex">
      <div [id]="'ant-button-select-carousel-' + tileIndex">

        <!-- Transcluded content -->
        <ng-content></ng-content>

      </div>
    </li>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DataDisplayCarouselTileComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "[data-uxd-data-display-tile-carousel-cmp]",
                    // styles: '[style.left.%]'= "scrollPos",
                    template: `
    <li class="button-select-content-container" tabindex="0" [attr.data-analytics]="attachAnalytics() + tileIndex"
    #CarouselItem [id]="'uxd-button-select-content-container-list-item-' + tileIndex">
      <div [id]="'ant-button-select-carousel-' + tileIndex">

        <!-- Transcluded content -->
        <ng-content></ng-content>

      </div>
    </li>
  `
                }]
        }], propDecorators: { tileIndex: [{
                type: Input
            }], buttonSelectorData: [{
                type: Input
            }], bindLeftPos: [{
                type: HostBinding,
                args: ["style.left.%"]
            }], buttonSelectItem: [{
                type: HostBinding,
                args: ["class.button-select-item"]
            }] } });
//istanbul ignore next
class DataDisplayCarouselComponent extends CarouselTemplateComponent {
    constructor() {
        super(...arguments);
        this.carouselSelectorClass = true;
    }
    ngAfterContentChecked() {
        if (this.selectorTileChildren.toArray().length > 0) {
            this._btnSelectorData = this.selectorTileChildren.toArray()[0].buttonSelectorData;
            this.leftCtrlAnalyticsId =
                this.btnSelectorData.controlAnalyticsTextLeft || "contractsSliderLeft2";
            this.rightCtrlAnalyticsId =
                this.btnSelectorData.controlAnalyticsTextRight ||
                    "contractsSliderRight2";
        }
        this.selectorTileChildren.toArray().forEach(selectTile => {
            selectTile.scrollPos = this.scrollPos;
            selectTile.maxVisible = this.getNumOfVisibleItems();
        });
    }
    set btnSelectorData(btnSelectDataObj) {
        this._btnSelectorData = btnSelectDataObj;
    }
    get btnSelectorData() {
        return this._btnSelectorData;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DataDisplayCarouselComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: DataDisplayCarouselComponent, selector: "[data-uxd-data-display-carousel-cmp]", host: { properties: { "class.carousel-selector-cmp": "this.carouselSelectorClass" } }, queries: [{ propertyName: "selectorTileChildren", predicate: DataDisplayCarouselTileComponent }], usesInheritance: true, ngImport: i0, template: `
    <div #LeftControl id="ant-button-selector-carousel-left-control" class="carousel-left-control" (click)="scroll('left')" [attr.data-analytics]="leftCtrlAnalyticsId">
        <button aria-label="Move carousel left" tabindex="-1" [attr.data-analytics]="leftCtrlAnalyticsId" class="fa fa-chevron-left" [class.hidden]="disableLeft" [attr.data-analytics]="leftCtrlAnalyticsId"></button>
    </div>

    <div #ViewPort class="carousel-content-container" id="ant-button-selector-carousel-tiles-container">
      <div *ngIf="items.length > 1" class="sr-only" [innerText]=""></div>
      <!-- btnSelectorData.ariaHowTo -->
      <ul #CarouselList role="tablist" [attr.aria-multiselectable]="items.length > 1" id="ant-button-selector-carousel-list-container">

        <!-- Transcluded content -->
        <ng-content></ng-content>

      </ul>
    </div>

    <div #RightControl id="ant-button-selector-carousel-right-control" class="carousel-right-control" (click)="scroll('right')" [attr.data-analytics]="rightCtrlAnalyticsId">
      <button aria-label="Move carousel right" tabindex="-1" [attr.data-analytics]="rightCtrlAnalyticsId" class="fa fa-chevron-right" [class.hidden]="disableRight" [attr.data-analytics]="rightCtrlAnalyticsId"></button>
    </div>
`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DataDisplayCarouselComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "[data-uxd-data-display-carousel-cmp]",
                    template: `
    <div #LeftControl id="ant-button-selector-carousel-left-control" class="carousel-left-control" (click)="scroll('left')" [attr.data-analytics]="leftCtrlAnalyticsId">
        <button aria-label="Move carousel left" tabindex="-1" [attr.data-analytics]="leftCtrlAnalyticsId" class="fa fa-chevron-left" [class.hidden]="disableLeft" [attr.data-analytics]="leftCtrlAnalyticsId"></button>
    </div>

    <div #ViewPort class="carousel-content-container" id="ant-button-selector-carousel-tiles-container">
      <div *ngIf="items.length > 1" class="sr-only" [innerText]=""></div>
      <!-- btnSelectorData.ariaHowTo -->
      <ul #CarouselList role="tablist" [attr.aria-multiselectable]="items.length > 1" id="ant-button-selector-carousel-list-container">

        <!-- Transcluded content -->
        <ng-content></ng-content>

      </ul>
    </div>

    <div #RightControl id="ant-button-selector-carousel-right-control" class="carousel-right-control" (click)="scroll('right')" [attr.data-analytics]="rightCtrlAnalyticsId">
      <button aria-label="Move carousel right" tabindex="-1" [attr.data-analytics]="rightCtrlAnalyticsId" class="fa fa-chevron-right" [class.hidden]="disableRight" [attr.data-analytics]="rightCtrlAnalyticsId"></button>
    </div>
`
                }]
        }], propDecorators: { carouselSelectorClass: [{
                type: HostBinding,
                args: ["class.carousel-selector-cmp"]
            }], selectorTileChildren: [{
                type: ContentChildren,
                args: [DataDisplayCarouselTileComponent]
            }] } });

class DefaultSelectorCarouselTileComponent {
    constructor() {
        this.onDefaultTileSelect = new EventEmitter();
    }
    ngAfterContentChecked() {
        if (this.defaultSelectorData.activeIndex === undefined) {
            this.defaultSelectorData.activeIndex = 0;
        }
        if (this.defaultSelectorData.render === undefined ||
            this.defaultSelectorData.render === "default") {
            this.defaultSelectTile = true;
            this.colorSelectTile = false;
        }
        else if (this.defaultSelectorData.render === "color") {
            this.colorSelectTile = true;
            this.defaultSelectTile = false;
        }
        this.activeIndex = this.defaultSelectorData.activeIndex;
        this.currentlySelected = this.activeIndex === this.tileIndex;
        this.bindLeftPos = this._scrollPos;
    }
    attachAnalytics() {
        if (this.defaultSelectorData.tileAnalyticId) {
            return this.defaultSelectorData.tileAnalyticId;
        }
        return null;
    }
    defaultSelectTileClick() {
        if (this.activeIndex !== this.tileIndex) {
            this.defaultSelectorData.activeIndex = this.tileIndex;
            this.activeIndex = this.defaultSelectorData.activeIndex;
        }
        this.emitDefaultSelectTileClick();
    }
    emitDefaultSelectTileClick() {
        if (this.defaultSelectorData) {
            let evtObj = {
                clickedIndex: this.tileIndex,
                defaultSelectTileData: this.defaultSelectorData[this.tileIndex]
            };
            this.onDefaultTileSelect.emit(evtObj);
        }
    }
    get activeIndex() {
        return this._activeIndex;
    }
    set activeIndex(index) {
        this._activeIndex = index;
    }
    get scrollPos() {
        return this._scrollPos;
    }
    set scrollPos(leftPerc) {
        this._scrollPos = leftPerc;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DefaultSelectorCarouselTileComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: DefaultSelectorCarouselTileComponent, selector: "[data-uxd-default-selector-carousel-tile-cmp]", inputs: { tileIndex: "tileIndex", defaultSelectorData: "defaultSelectorData" }, outputs: { onDefaultTileSelect: "onDefaultTileSelect" }, host: { properties: { "class.default-select-item-container": "this.defaultSelectTile", "class.color-select-item-container": "this.colorSelectTile", "class.active": "this.currentlySelected", "style.left.%": "this.bindLeftPos" } }, ngImport: i0, template: `
    <li role="tab" tabindex="0" #CarouselItem
    class="select-item"
    [attr.aria-selected] ="tileIndex === activeIndex"
    [id]="'ant-default-selector-carousel' + tileIndex"
    [attr.data-analytics]="attachAnalytics() + tileIndex"
    (keydown.enter)="defaultSelectTileClick()"
    (keyup.space)="defaultSelectTileClick()"
    (click)="defaultSelectTileClick()">

      <ng-content></ng-content>

    </li>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DefaultSelectorCarouselTileComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "[data-uxd-default-selector-carousel-tile-cmp]",
                    template: `
    <li role="tab" tabindex="0" #CarouselItem
    class="select-item"
    [attr.aria-selected] ="tileIndex === activeIndex"
    [id]="'ant-default-selector-carousel' + tileIndex"
    [attr.data-analytics]="attachAnalytics() + tileIndex"
    (keydown.enter)="defaultSelectTileClick()"
    (keyup.space)="defaultSelectTileClick()"
    (click)="defaultSelectTileClick()">

      <ng-content></ng-content>

    </li>
  `
                }]
        }], propDecorators: { tileIndex: [{
                type: Input
            }], defaultSelectorData: [{
                type: Input
            }], defaultSelectTile: [{
                type: HostBinding,
                args: ["class.default-select-item-container"]
            }], colorSelectTile: [{
                type: HostBinding,
                args: ["class.color-select-item-container"]
            }], currentlySelected: [{
                type: HostBinding,
                args: ["class.active"]
            }], bindLeftPos: [{
                type: HostBinding,
                args: ["style.left.%"]
            }], onDefaultTileSelect: [{
                type: Output
            }] } });
//istanbul ignore next
class DefaultSelectorCarouselComponent extends CarouselTemplateComponent {
    constructor() {
        super(...arguments);
        this.carouselSelectorClass = true;
    }
    ngAfterContentChecked() {
        if (this.selectorTileChildren.toArray().length > 0) {
            this._defaultSelectorData = this.selectorTileChildren.toArray()[0].defaultSelectorData;
        }
        this.selectorTileChildren.toArray().forEach(selectTile => {
            selectTile.scrollPos = this.scrollPos;
        });
    }
    set btnSelectorData(defaultSelectorDataObj) {
        this._defaultSelectorData = defaultSelectorDataObj;
    }
    get btnSelectorData() {
        return this._defaultSelectorData;
    }
    attachAnalytics(arrowName) {
        if (arrowName === "left" && this._defaultSelectorData.leftCtrlAnalyticsId) {
            return this._defaultSelectorData.leftCtrlAnalyticsId;
        }
        else if (arrowName === "right" &&
            this._defaultSelectorData.rightCtrlAnalyticsId) {
            return this._defaultSelectorData.rightCtrlAnalyticsId;
        }
    }
    getNumOfVisibleItems() {
        if (this.viewMode === CarouselTemplateComponent.MOBILE) {
            return 1;
        }
        if (this.viewMode === CarouselTemplateComponent.TABLET) {
            return 3;
        }
        // Default for desktop
        return 4;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DefaultSelectorCarouselComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: DefaultSelectorCarouselComponent, selector: "[data-uxd-default-selector-carousel-cmp]", host: { properties: { "class.carousel-selector-cmp": "this.carouselSelectorClass" } }, queries: [{ propertyName: "selectorTileChildren", predicate: DefaultSelectorCarouselTileComponent }], usesInheritance: true, ngImport: i0, template: `
    <div aria-hidden="true" #LeftControl id="ant-default-selector-carousel-left-control" class="carousel-left-control" (click)="scroll('left')" [attr.data-analytics]="attachAnalytics('left')">
        <button aria-label="Move carousel left" tabindex="-1" [attr.data-analytics]="attachAnalytics('left')" class="fa fa-chevron-left" [class.hidden]="disableLeft" [attr.data-analytics]="attachAnalytics('left')"></button>
    </div>

    <div #ViewPort class="carousel-content-container" id="ant-default-selector-carousel-tiles-container">
      <div *ngIf="items.length > 1" class="sr-only" [innerText]=""></div>
      <!-- btnSelectorData.ariaHowTo -->
      <ul #CarouselList role="tablist" [attr.aria-multiselectable]="items.length > 1" id="ant-default-selector-carousel-list-container">

        <!-- Transcluded content -->
        <ng-content></ng-content>

      </ul>
    </div>

    <div aria-hidden="true" #RightControl id="ant-default-selector-carousel-right-control" class="carousel-right-control " (click)="scroll('right')" [attr.data-analytics]="attachAnalytics('right')">
        <button aria-label="Move carousel right" tabindex="-1" [attr.data-analytics]="attachAnalytics('right')"  class="fa fa-chevron-right" [class.hidden]="disableRight" [attr.data-analytics]="attachAnalytics('right')"></button>
    </div>
`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DefaultSelectorCarouselComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "[data-uxd-default-selector-carousel-cmp]",
                    template: `
    <div aria-hidden="true" #LeftControl id="ant-default-selector-carousel-left-control" class="carousel-left-control" (click)="scroll('left')" [attr.data-analytics]="attachAnalytics('left')">
        <button aria-label="Move carousel left" tabindex="-1" [attr.data-analytics]="attachAnalytics('left')" class="fa fa-chevron-left" [class.hidden]="disableLeft" [attr.data-analytics]="attachAnalytics('left')"></button>
    </div>

    <div #ViewPort class="carousel-content-container" id="ant-default-selector-carousel-tiles-container">
      <div *ngIf="items.length > 1" class="sr-only" [innerText]=""></div>
      <!-- btnSelectorData.ariaHowTo -->
      <ul #CarouselList role="tablist" [attr.aria-multiselectable]="items.length > 1" id="ant-default-selector-carousel-list-container">

        <!-- Transcluded content -->
        <ng-content></ng-content>

      </ul>
    </div>

    <div aria-hidden="true" #RightControl id="ant-default-selector-carousel-right-control" class="carousel-right-control " (click)="scroll('right')" [attr.data-analytics]="attachAnalytics('right')">
        <button aria-label="Move carousel right" tabindex="-1" [attr.data-analytics]="attachAnalytics('right')"  class="fa fa-chevron-right" [class.hidden]="disableRight" [attr.data-analytics]="attachAnalytics('right')"></button>
    </div>
`
                }]
        }], propDecorators: { carouselSelectorClass: [{
                type: HostBinding,
                args: ["class.carousel-selector-cmp"]
            }], selectorTileChildren: [{
                type: ContentChildren,
                args: [DefaultSelectorCarouselTileComponent]
            }] } });

class ProgressBarComponent extends CarouselTemplateComponent {
    constructor() {
        super(...arguments);
        this.onChange = new EventEmitter();
        this.currentIndex = 0;
        this.optionalSelected = false;
    }
    ngOnInit() {
        this.progressBar.days = this.progressBar.days
            ? this.progressBar.days
            : [30, 90];
        this.progressBarModel = this.progressBar.options[0].savingsValue;
        this.alternateDrug = this.progressBar.options[0].label;
        this.alternateDrugValue = this.progressBar.options[0].retailValue;
        this.alternateDrugBrand = this.progressBar.options[0].brand;
    }
    _onChangeCallback(value) {
        // noop
    }
    _onTouchedCallback() {
        // noop
    }
    // constructor(private _renderer: Renderer2) {
    // }
    // ngAfterViewChecked() {
    // }
    ngAfterViewInit() {
        this.carousel.getNumOfVisibleItems = this.getNumOfVisibleItems2;
        this.carousel.resetScroll();
    }
    ngOnChanges(changes) {
        //noop
    }
    onItemClick(index) {
        this.currentIndex = index;
        this.onChange.emit(index);
        this.alternateDrug = this.progressBar.options[index].label;
        this.progressBarModel = this.progressBar.options[index].savingsValue;
        this.alternateDrugValue = this.progressBar.options[index].retailValue;
        this.alternateDrugBrand = this.progressBar.options[index].brand;
    }
    onOptionalFocus() {
        this.optionalSelected = true;
        this.setSelectedValue("");
        this._onChangeCallback("");
    }
    onItemFocus(item) {
        item.isFocused = true;
    }
    onItemBlur(item) {
        item.isFocused = false;
        this._onTouchedCallback();
    }
    writeValue(v) {
        this.setSelectedValue(v);
    }
    registerOnChange(fn) {
        //todo
        this._onChangeCallback = fn;
    }
    registerOnTouched(fn) {
        //todo
        this._onTouchedCallback = fn;
    }
    setSelectedValue(value = "") {
        value = value === null || typeof value === "undefined" ? "" : value;
        this.optionalSelected = false;
        this.progressBar.options.forEach((item) => {
            if (item.value.toString() === value.toString()) {
                item.isSelected = true;
            }
            else {
                item.isSelected = false;
            }
        });
    }
    getNumOfVisibleItems2() {
        if (this.viewMode === CarouselTemplateComponent.MOBILE) {
            return 2;
        }
        if (this.viewMode === CarouselTemplateComponent.TABLET) {
            return 4;
        }
        // Default for desktop
        return 6;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ProgressBarComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ProgressBarComponent, selector: "[data-uxd-progress-bar-cmp]", inputs: { progressBar: ["progressContent", "progressBar"] }, outputs: { onChange: "onChange" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => ProgressBarComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "carousel", first: true, predicate: DefaultSelectorCarouselComponent, descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: `<fieldset class="ant-progress-bar-cmp">
                <legend [id]="'lgnd_'+progressBar.id" class="" [class.ant-span-label]="progressBar.showLegendAsLbl" [class.sr-only]="progressBar.legendSrOnly" [innerHTML]="progressBar.label"></legend>
                <div>
                <div *ngIf = "progressBar.options.length > 1"  data-uxd-default-selector-carousel-cmp [items]="progressBar.options">
                    <div data-uxd-default-selector-carousel-tile-cmp *ngFor="let option of progressBar.options; let i = index"
                    [tileIndex]="i" [defaultSelectorData]="progressBar.options" (onDefaultTileSelect)="onItemClick(i)">
                      <div class="text-center">
                      <div class="care-gap-buttons" [class.active]="currentIndex == i">
                        <input [(ngModel)]="progressBarModel" tabindex="-1" [value]="option.savingsValue" [attr.value]="option.value"  [attr.aria-label]="option.ariaLabel" (focus)="onItemFocus(option)" (blur)="onItemBlur(option)"  class="prOption" type="radio" [name]="progressBar.name" [id]="progressBar.id+i"  />
                        <label [attr.data-analytics] = "progressBar.analyticsText" [class.active]="currentIndex === i" [id]="'lbl_'+progressBar.id+i"   class="prLabel" [attr.for]="progressBar.id+i" [innerHTML]="option.label"></label>
                      </div>
                      </div>
                    </div>
                  </div>
                  <br/><br/>
                  <div class="row text-center">
                    <div class="col-lg-offset-2 col-lg-3 col-md-offset-1 col-md-4 col-sm-offset-1 col-sm-4 col-xs-offset-0 col-xs-5 currentDrugView">
                      <span class="current-drug-text" tabindex="0">Current Medication</span>
                      <div class="current-drug-label" tabindex="0">{{progressBar.currentDrug}}</div>
                      <div class="price-brand">$ {{progressBar.currentDrugPrice}}</div>
                      <div class="guarantee"> 1 year {{progressBar.currenDrugBrand}} cost</div>
                    </div>
                    <div class="col-xs-offset-1 col-xs-5 alternateDrugView divider">
                      <span class="alternate-drug-text" tabindex="0">Alternative Medication</span>
                      <div class="alternate-drug-label" tabindex="0">{{ alternateDrug }}</div>
                      <div class="price-generic">$ {{ alternateDrugValue}}</div>
                      <div class="guarantee"> 1 year {{alternateDrugBrand}} cost</div>
                    </div>
                  </div>
                  <br><br>
                  <div class="row text-center">
                    <h3>Let's take a look at long term savings for {{alternateDrug}}</h3>
                    </div>
                    <br><br>
                  <div class="ant-progress-bar-full info">
                    <div class="ant-progress-bar ant-progress-bar-thirty" role="progressbar" *ngFor="let day of progressBar.days">
                      <div class="ant-value-info">
                        <div class="ant-progress-saved">$ {{(progressBarModel/365)*day|number:'1.2-2'}}</div>
                        <div class="ant-progress-days">{{day}} Days</div>
                      </div>
                    </div>

                    <div role="progressbar">
                      <div class="ant-value-yearly">
                        <div class="ant-progress-saved">$ {{progressBarModel}}</div>
                        <div class="ant-progress-days">Yearly</div>
                      </div>
                    </div>
                  </div>
                  <div class="ant-progress-disclaimer">
                    <p>*Out-of-pocket costs based on your current plan</p>
                  </div>
                  <input class="sr-only" *ngIf="progressBar.isOptional" (focus)="onOptionalFocus()" [checked]="optionalSelected" class="prOption" type="radio" [name]="progressBar.name" [id]="progressBar.id+'_opt'" [value]="" [attr.value]="" />
                </div>
              </fieldset>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: DefaultSelectorCarouselComponent, selector: "[data-uxd-default-selector-carousel-cmp]" }, { kind: "component", type: DefaultSelectorCarouselTileComponent, selector: "[data-uxd-default-selector-carousel-tile-cmp]", inputs: ["tileIndex", "defaultSelectorData"], outputs: ["onDefaultTileSelect"] }, { kind: "pipe", type: i1.DecimalPipe, name: "number" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ProgressBarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "[data-uxd-progress-bar-cmp]",
                    template: `<fieldset class="ant-progress-bar-cmp">
                <legend [id]="'lgnd_'+progressBar.id" class="" [class.ant-span-label]="progressBar.showLegendAsLbl" [class.sr-only]="progressBar.legendSrOnly" [innerHTML]="progressBar.label"></legend>
                <div>
                <div *ngIf = "progressBar.options.length > 1"  data-uxd-default-selector-carousel-cmp [items]="progressBar.options">
                    <div data-uxd-default-selector-carousel-tile-cmp *ngFor="let option of progressBar.options; let i = index"
                    [tileIndex]="i" [defaultSelectorData]="progressBar.options" (onDefaultTileSelect)="onItemClick(i)">
                      <div class="text-center">
                      <div class="care-gap-buttons" [class.active]="currentIndex == i">
                        <input [(ngModel)]="progressBarModel" tabindex="-1" [value]="option.savingsValue" [attr.value]="option.value"  [attr.aria-label]="option.ariaLabel" (focus)="onItemFocus(option)" (blur)="onItemBlur(option)"  class="prOption" type="radio" [name]="progressBar.name" [id]="progressBar.id+i"  />
                        <label [attr.data-analytics] = "progressBar.analyticsText" [class.active]="currentIndex === i" [id]="'lbl_'+progressBar.id+i"   class="prLabel" [attr.for]="progressBar.id+i" [innerHTML]="option.label"></label>
                      </div>
                      </div>
                    </div>
                  </div>
                  <br/><br/>
                  <div class="row text-center">
                    <div class="col-lg-offset-2 col-lg-3 col-md-offset-1 col-md-4 col-sm-offset-1 col-sm-4 col-xs-offset-0 col-xs-5 currentDrugView">
                      <span class="current-drug-text" tabindex="0">Current Medication</span>
                      <div class="current-drug-label" tabindex="0">{{progressBar.currentDrug}}</div>
                      <div class="price-brand">$ {{progressBar.currentDrugPrice}}</div>
                      <div class="guarantee"> 1 year {{progressBar.currenDrugBrand}} cost</div>
                    </div>
                    <div class="col-xs-offset-1 col-xs-5 alternateDrugView divider">
                      <span class="alternate-drug-text" tabindex="0">Alternative Medication</span>
                      <div class="alternate-drug-label" tabindex="0">{{ alternateDrug }}</div>
                      <div class="price-generic">$ {{ alternateDrugValue}}</div>
                      <div class="guarantee"> 1 year {{alternateDrugBrand}} cost</div>
                    </div>
                  </div>
                  <br><br>
                  <div class="row text-center">
                    <h3>Let's take a look at long term savings for {{alternateDrug}}</h3>
                    </div>
                    <br><br>
                  <div class="ant-progress-bar-full info">
                    <div class="ant-progress-bar ant-progress-bar-thirty" role="progressbar" *ngFor="let day of progressBar.days">
                      <div class="ant-value-info">
                        <div class="ant-progress-saved">$ {{(progressBarModel/365)*day|number:'1.2-2'}}</div>
                        <div class="ant-progress-days">{{day}} Days</div>
                      </div>
                    </div>

                    <div role="progressbar">
                      <div class="ant-value-yearly">
                        <div class="ant-progress-saved">$ {{progressBarModel}}</div>
                        <div class="ant-progress-days">Yearly</div>
                      </div>
                    </div>
                  </div>
                  <div class="ant-progress-disclaimer">
                    <p>*Out-of-pocket costs based on your current plan</p>
                  </div>
                  <input class="sr-only" *ngIf="progressBar.isOptional" (focus)="onOptionalFocus()" [checked]="optionalSelected" class="prOption" type="radio" [name]="progressBar.name" [id]="progressBar.id+'_opt'" [value]="" [attr.value]="" />
                </div>
              </fieldset>`,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => ProgressBarComponent),
                            multi: true
                        }
                    ]
                }]
        }], propDecorators: { progressBar: [{
                type: Input,
                args: ["progressContent"]
            }], onChange: [{
                type: Output,
                args: ["onChange"]
            }], carousel: [{
                type: ViewChild,
                args: [DefaultSelectorCarouselComponent]
            }] } });

class CarouselModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CarouselModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: CarouselModule, declarations: [ButtonSelectorCarouselTileComponent,
            ButtonSelectorCarouselComponentV2,
            CarouselTemplateComponent,
            DataDisplayCarouselComponent,
            DataDisplayCarouselTileComponent,
            DefaultSelectorCarouselComponent,
            DefaultSelectorCarouselTileComponent,
            ProgressBarComponent], imports: [CommonModule, FormsModule], exports: [ButtonSelectorCarouselTileComponent,
            ButtonSelectorCarouselComponentV2,
            DataDisplayCarouselComponent,
            DataDisplayCarouselTileComponent,
            CarouselTemplateComponent,
            DefaultSelectorCarouselComponent,
            DefaultSelectorCarouselTileComponent,
            ProgressBarComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CarouselModule, imports: [CommonModule, FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CarouselModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule],
                    declarations: [
                        ButtonSelectorCarouselTileComponent,
                        ButtonSelectorCarouselComponentV2,
                        CarouselTemplateComponent,
                        DataDisplayCarouselComponent,
                        DataDisplayCarouselTileComponent,
                        DefaultSelectorCarouselComponent,
                        DefaultSelectorCarouselTileComponent,
                        ProgressBarComponent
                    ],
                    exports: [
                        ButtonSelectorCarouselTileComponent,
                        ButtonSelectorCarouselComponentV2,
                        DataDisplayCarouselComponent,
                        DataDisplayCarouselTileComponent,
                        CarouselTemplateComponent,
                        DefaultSelectorCarouselComponent,
                        DefaultSelectorCarouselTileComponent,
                        ProgressBarComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ButtonSelectorCarouselComponentV2, ButtonSelectorCarouselTileComponent, CarouselModule, CarouselTemplateComponent, DataDisplayCarouselComponent, DataDisplayCarouselTileComponent, DefaultSelectorCarouselComponent, DefaultSelectorCarouselTileComponent, ProgressBarComponent };
